import { Box, Grid } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { ArrowRepeat, ShippingBoxV2 } from "akar-icons";
import React, { useEffect, useState } from "react";

import { ErrorBoundary } from "@customer-app/account/views/OperationAquarius/components/ErrorBoundary";
import { openModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { ManageSubscriptionCard } from "./libs/components/ManageSubscriptionCard";
import { useActiveTab } from "./libs/utils/useActiveTab";
import { useSmartrrTheme } from "../../../../hooks/useSmartrrTheme";
import { useSortedPurchaseStates } from "../../../hooks/useSortedPurchaseStates";
import { ISubscriptionProps, StyledTab, StyledTabWrapper } from "../constants";
import { getDeliveryForCPS } from "../V2SubscriptionManagement/libs/utils";
import { YourNextOrderAndAddons } from "../YourNextOrderAndAddons";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const V1SubscriptionManagement = ({ upcomingDelivery }: ISubscriptionProps) => {
  const [activeTab, setActiveTab] = useState<number>(upcomingDelivery ? 0 : 1);
  const theme = useSmartrrTheme();
  const dispatch = useSmartrrCustomerDispatch();
  const sortedPurchaseStates = useSortedPurchaseStates();
  const { sellingPlansIsLoading } = useSmartrrCustomerSelector(state => state.sellingPlanGroups);
  const { organization, customerRelationship } = useSmartrrCustomerSelector(state => state.auth);

  let cardIterator = 0;
  const tabChangeHandler = (event: React.SyntheticEvent, index: number) => {
    setActiveTab(index);
  };

  useActiveTab(setActiveTab);
  useEffect(() => {
    const params = new URL(document.location as unknown as string).searchParams;
    const subscriptionId = params.get("subscription");
    if (subscriptionId && !sellingPlansIsLoading && organization && customerRelationship?.isSuperUser) {
      const purchaseState = sortedPurchaseStates.find(ps => ps[1][0].shopifyId?.includes(subscriptionId))?.[1][0];

      if (!purchaseState || purchaseState.purchaseStateStatus !== "PAUSED") {
        return;
      }

      const del = getDeliveryForCPS(purchaseState, organization);
      if (del) {
        dispatch(
          openModal("MANAGE_SUBSCRIPTIONS", {
            deliveryId: getDeliveryId(del),
            initialView: "Pause",
            generatePurchasables: false,
            purchasablesForAdding: [],
          })
        );
      }
    }
  }, [sellingPlansIsLoading, customerRelationship?.isSuperUser]);

  function a11yProps(index: number) {
    return {
      id: `subscription-tab-${index}`,
      "aria-controls": `subscription-tabpanel-${index}`,
    };
  }

  return (
    <React.Fragment>
      <Box className={prefixClassName("subscription-wrapper")} sx={{ position: "relative" }}>
        <Box sx={{ marginLeft: theme.modernCardRadius + "px" }}>
          <StyledTabWrapper
            value={upcomingDelivery ? activeTab : 0}
            onChange={tabChangeHandler}
            className={prefixClassName("tab-wrapper")}
          >
            {upcomingDelivery ? (
              <StyledTab
                label={typedLocalize("tabs.nextOrderTab")}
                icon={<ShippingBoxV2 />}
                className={prefixClassName("tab-button-0")}
                iconPosition="start"
                {...a11yProps(0)}
              />
            ) : null}
            <StyledTab
              label={typedLocalize("tabs.subscriptionTab")}
              icon={<ArrowRepeat />}
              className={prefixClassName("tab-button-1")}
              iconPosition="start"
              {...a11yProps(1)}
            />
          </StyledTabWrapper>
        </Box>
        {upcomingDelivery ? (
          <ErrorBoundary>
            <Box aria-labelledby="subscription-tabpanel-0" hidden={activeTab !== 0}>
              <YourNextOrderAndAddons upcomingDelivery={upcomingDelivery} />
            </Box>
          </ErrorBoundary>
        ) : null}
        <ErrorBoundary>
          <Box aria-labelledby="subscription-tabpanel-1" hidden={upcomingDelivery ? activeTab !== 1 : false}>
            <Grid container spacing={"20px"}>
              {sortedPurchaseStates.map(group =>
                group[1].map(purchaseState => {
                  cardIterator += 1;
                  return (
                    <ManageSubscriptionCard
                      key={purchaseState.id}
                      purchaseState={purchaseState}
                      subscriptionNumber={cardIterator}
                    />
                  );
                })
              )}
            </Grid>
          </Box>
        </ErrorBoundary>
      </Box>
    </React.Fragment>
  );
};
